import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UploadSimple, Plus, Package, Stack, Clock, CheckCircle, XCircle, CurrencyDollar, DownloadSimple } from "@phosphor-icons/react";

const TYPES = [
  { value: "icon_pack", label: "Icon Pack" },
  { value: "widget", label: "Widget" },
  { value: "wallpaper", label: "Wallpaper" },
];
const inputCls =
  "w-full bg-[#05050A] border border-white/10 rounded-sm px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#00E5FF] transition-colors";
const emptyItem = { type: "icon_pack", title: "", description: "", category: "", preview_image: "" };
const emptyBundle = { title: "", description: "", cover_image: "", item_ids: [] };
const statusStyle = {
  approved: { c: "#00E5FF", Icon: CheckCircle, label: "Live" },
  pending: { c: "#FFEB3B", Icon: Clock, label: "In review" },
  rejected: { c: "#FF00AA", Icon: XCircle, label: "Rejected" },
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function Submit() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("item");
  const [itemForm, setItemForm] = useState(emptyItem);
  const [bundleForm, setBundleForm] = useState(emptyBundle);
  const [myItems, setMyItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState({ items: [], bundles: [] });
  const [earnings, setEarnings] = useState(null);

  const approvedItems = myItems.filter((i) => i.status === "approved");

  const load = () => {
    api.get("/creator/submissions").then((r) => {
      setSubs(r.data);
      setMyItems(r.data.items);
    }).catch(() => {});
    api.get("/creator/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/login");
      } else if (user.creator_status !== "approved" && !user.is_admin) {
        toast.error("Approved creators only");
        navigate("/account");
      } else {
        load();
      }
    }
  }, [user, loading]); // eslint-disable-line

  const handleUpload = async (e, setter, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter((p) => ({ ...p, [field]: `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitItem = async (e) => {
    e.preventDefault();
    if (!itemForm.title || !itemForm.preview_image) return toast.error("Title and preview image required");
    setSaving(true);
    try {
      await api.post("/creator/items", itemForm);
      toast.success("Submitted for review!");
      setItemForm(emptyItem);
      load();
    } catch {
      toast.error("Submission failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (id) =>
    setBundleForm((p) => ({
      ...p,
      item_ids: p.item_ids.includes(id) ? p.item_ids.filter((x) => x !== id) : [...p.item_ids, id],
    }));

  const submitBundle = async (e) => {
    e.preventDefault();
    if (!bundleForm.title || !bundleForm.cover_image || bundleForm.item_ids.length < 2)
      return toast.error("Title, cover, and at least 2 of your approved items required");
    setSaving(true);
    try {
      await api.post("/creator/bundles", bundleForm);
      toast.success("Bundle submitted for review!");
      setBundleForm(emptyBundle);
      load();
    } catch {
      toast.error("Submission failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;

  const allSubs = [
    ...subs.items.map((i) => ({ ...i, kind: "Item" })),
    ...subs.bundles.map((b) => ({ ...b, kind: "Bundle" })),
  ];

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <UploadSimple weight="bold" size={22} className="text-[#00E5FF]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Creator Studio</span>
        </div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter uppercase">Submit your work</h1>
        <p className="text-white/50 mt-3 max-w-xl">Everything you submit goes to an admin for review before it goes live in the marketplace.</p>

        <div className="flex gap-2 mt-8 border-b border-white/10">
          {[
            { key: "item", label: "Submit Pack", icon: Package },
            { key: "bundle", label: "Submit Bundle", icon: Stack },
            { key: "earnings", label: "Earnings", icon: CurrencyDollar },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`submit-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                  tab === t.key ? "border-[#00E5FF] text-[#00E5FF]" : "border-transparent text-white/40 hover:text-white/70"
                }`}>
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "earnings" ? (
          <div className="mt-8" data-testid="earnings-panel">
            {!earnings ? (
              <p className="text-white/40">Loading earnings…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Total Earnings", value: `$${earnings.total_earnings.toFixed(2)}`, color: "#FFEB3B", testid: "earnings-total" },
                    { label: "Total Downloads", value: earnings.total_downloads, color: "#00E5FF" },
                    { label: "Approved Packs", value: earnings.approved_items, color: "#FF00AA" },
                    { label: "Your Share", value: `${earnings.creator_share_pct}%`, color: "#FFEB3B" },
                  ].map((s) => (
                    <div key={s.label} className="bg-[#0A0A14] border border-white/10 rounded-sm p-5">
                      <div className="font-display font-black text-3xl" style={{ color: s.color }} data-testid={s.testid}>{s.value}</div>
                      <div className="text-white/40 text-xs uppercase tracking-wider mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-white/40 text-xs mt-4">
                  You earn ${earnings.rate_per_download.toFixed(2)} per download (${earnings.revenue_per_download.toFixed(2)} × {earnings.creator_share_pct}% creator share).
                </p>
                <div className="mt-8 bg-[#0A0A14] border border-white/10 rounded-sm overflow-hidden" data-testid="earnings-table">
                  <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[11px] font-bold uppercase tracking-wider text-white/40">
                    <div className="col-span-7">Pack</div>
                    <div className="col-span-3 text-right">Downloads</div>
                    <div className="col-span-2 text-right">Earnings</div>
                  </div>
                  {earnings.lines.length === 0 && (
                    <p className="text-white/40 text-sm p-4">No approved packs yet. Once your submissions are approved and downloaded, earnings appear here.</p>
                  )}
                  {earnings.lines.map((l) => (
                    <div key={l.id} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5" data-testid={`earnings-row-${l.id}`}>
                      <div className="col-span-7 flex items-center gap-3 min-w-0">
                        <img src={l.preview_image} alt="" className="w-10 h-10 object-cover rounded-sm shrink-0" />
                        <span className="font-bold truncate">{l.title}</span>
                      </div>
                      <div className="col-span-3 text-right flex items-center justify-end gap-1 text-white/70">
                        <DownloadSimple size={14} /> {l.downloads}
                      </div>
                      <div className="col-span-2 text-right font-bold text-[#FFEB3B]">${l.earnings.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {tab === "item" ? (
            <form onSubmit={submitItem} className="bg-[#0A0A14] border border-white/10 rounded-sm p-6 space-y-4" data-testid="creator-item-form">
              <h3 className="font-display font-bold uppercase tracking-tight flex items-center gap-2"><Plus size={18} /> New Pack</h3>
              <Field label="Type">
                <select value={itemForm.type} onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })} className={inputCls} data-testid="creator-item-type">
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Title">
                <input value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} className={inputCls} data-testid="creator-item-title" />
              </Field>
              <Field label="Category">
                <input value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} className={inputCls} placeholder="e.g. Icon Pack" />
              </Field>
              <Field label="Description">
                <textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className={inputCls} rows={3} />
              </Field>
              <Field label="Preview Image">
                <div className="flex gap-2">
                  <input value={itemForm.preview_image} onChange={(e) => setItemForm({ ...itemForm, preview_image: e.target.value })} className={inputCls} placeholder="Paste URL or upload" data-testid="creator-item-image" />
                  <label className="shrink-0 flex items-center justify-center w-11 bg-[#05050A] border border-white/10 rounded-sm cursor-pointer hover:border-[#00E5FF] transition-colors">
                    <UploadSimple size={18} className={uploading ? "animate-pulse text-[#00E5FF]" : "text-white/60"} />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, setItemForm, "preview_image")} data-testid="creator-item-upload" />
                  </label>
                </div>
                {itemForm.preview_image && <img src={itemForm.preview_image} alt="" className="mt-3 w-full aspect-video object-cover rounded-sm border border-white/10" />}
              </Field>
              <button type="submit" disabled={saving} className="w-full bg-[#00E5FF] text-[#05050A] font-bold py-3 rounded-sm hover:bg-[#5cf0ff] transition-colors disabled:opacity-60" data-testid="creator-item-submit">
                {saving ? "Submitting…" : "Submit for Review"}
              </button>
            </form>
          ) : (
            <form onSubmit={submitBundle} className="bg-[#0A0A14] border border-white/10 rounded-sm p-6 space-y-4" data-testid="creator-bundle-form">
              <h3 className="font-display font-bold uppercase tracking-tight flex items-center gap-2"><Plus size={18} /> New Bundle</h3>
              <Field label="Title">
                <input value={bundleForm.title} onChange={(e) => setBundleForm({ ...bundleForm, title: e.target.value })} className={inputCls} data-testid="creator-bundle-title" />
              </Field>
              <Field label="Description">
                <textarea value={bundleForm.description} onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })} className={inputCls} rows={3} />
              </Field>
              <Field label="Cover Image">
                <div className="flex gap-2">
                  <input value={bundleForm.cover_image} onChange={(e) => setBundleForm({ ...bundleForm, cover_image: e.target.value })} className={inputCls} placeholder="Paste URL or upload" data-testid="creator-bundle-cover" />
                  <label className="shrink-0 flex items-center justify-center w-11 bg-[#05050A] border border-white/10 rounded-sm cursor-pointer hover:border-[#00E5FF] transition-colors">
                    <UploadSimple size={18} className={uploading ? "animate-pulse text-[#00E5FF]" : "text-white/60"} />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, setBundleForm, "cover_image")} />
                  </label>
                </div>
              </Field>
              <Field label={`Your approved items (${bundleForm.item_ids.length} selected)`}>
                {approvedItems.length === 0 ? (
                  <p className="text-white/40 text-xs">You need at least 2 approved packs to build a bundle.</p>
                ) : (
                  <div className="max-h-56 overflow-y-auto border border-white/10 rounded-sm divide-y divide-white/5">
                    {approvedItems.map((it) => (
                      <label key={it.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5">
                        <input type="checkbox" checked={bundleForm.item_ids.includes(it.id)} onChange={() => toggleItem(it.id)} className="accent-[#FFEB3B]" />
                        <img src={it.preview_image} alt="" className="w-8 h-8 object-cover rounded-sm" />
                        <span className="text-sm truncate flex-1">{it.title}</span>
                      </label>
                    ))}
                  </div>
                )}
              </Field>
              <button type="submit" disabled={saving} className="w-full bg-[#00E5FF] text-[#05050A] font-bold py-3 rounded-sm hover:bg-[#5cf0ff] transition-colors disabled:opacity-60" data-testid="creator-bundle-submit">
                {saving ? "Submitting…" : "Submit Bundle"}
              </button>
            </form>
          )}

          {/* My submissions */}
          <div className="lg:col-span-2">
            <h3 className="font-display font-bold uppercase tracking-tight mb-4">My Submissions</h3>
            <div className="space-y-3" data-testid="my-submissions">
              {allSubs.length === 0 && <p className="text-white/40 text-sm">Nothing submitted yet.</p>}
              {allSubs.map((s) => {
                const st = statusStyle[s.status] || statusStyle.pending;
                const Icon = st.Icon;
                return (
                  <div key={s.id} className="bg-[#0A0A14] border border-white/10 rounded-sm p-3 flex items-center gap-4">
                    <img src={s.preview_image || s.cover_image} alt="" className="w-14 h-14 object-cover rounded-sm shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{s.title}</p>
                      <p className="text-white/40 text-xs">{s.kind}</p>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider shrink-0" style={{ color: st.c }}>
                      <Icon weight="fill" size={16} /> {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
